import { useEffect, useMemo, useState } from "react";
import { INK, MUTED_36, MUTED_60, FONT, ROOMS, glow, capsuleStyle } from "@/lib/yuoDesign";
import { api } from "@/lib/api";

/**
 * Notes from the Library.  Dark glow panel.
 */
export function MyNotesSheet({ visible, onClose, onInsert, accentColor }) {
  const accent = accentColor || ROOMS.library.accent;
  const accentRgb = accent && accent.startsWith("#")
    ? [1,3,5].map((i) => parseInt(accent.slice(i, i+2), 16)).join(",")
    : ROOMS.library.rgb;
  const [breakdowns, setBreakdowns] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [bs, ns] = await Promise.all([api.listBreakdowns(), api.listMarginNotes()]);
        if (cancelled) return;
        setBreakdowns(bs?.breakdowns || []);
        setNotes(ns?.notes || []);
      } catch { /* ignore */ }
      finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [visible]);

  const items = useMemo(() => {
    const merged = [
      ...breakdowns.map((b) => ({
        kind: "breakdown",
        id: b.breakdown_id,
        ref_human: b.ref_human || b.ref,
        verse_text: b.verse_text,
        body: b.breakdown,
        created_at: b.created_at,
      })),
      ...notes.map((n) => ({
        kind: "note",
        id: n.note_id,
        ref_human: `${n.code} ${n.chapter}:${n.verse}`,
        verse_text: null,
        body: n.note,
        created_at: n.created_at,
      })),
    ];
    merged.sort((a, b) => {
      const at = new Date(a.created_at || 0).getTime();
      const bt = new Date(b.created_at || 0).getTime();
      return bt - at;
    });
    if (!query.trim()) return merged;
    const q = query.trim().toLowerCase();
    return merged.filter((it) => {
      return (
        (it.ref_human || "").toLowerCase().includes(q) ||
        (it.verse_text || "").toLowerCase().includes(q) ||
        (it.body || "").toLowerCase().includes(q)
      );
    });
  }, [breakdowns, notes, query]);

  if (!visible) return null;

  return (
    <div className="yuo-modal-backdrop" onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 90,
        background: "var(--dk-3410-72)", backdropFilter: "blur(14px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="my-notes-sheet"
        style={{
          width: "100%", maxWidth: 560, maxHeight: "85vh",
          padding: 24, borderRadius: 28,
          background: `${glow(accentRgb, 0.14)}, var(--yuo-bg)`,
          boxShadow: `0 0 60px rgba(${accentRgb},0.22), 0 20px 60px rgba(0,0,0,0.55)`,
          fontFamily: FONT, color: INK,
          display: "flex", flexDirection: "column",
        }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div style={{ color: accent, fontSize: 10.5, letterSpacing: "0.28em", textTransform: "uppercase", marginBottom: 4 }}>
              My Book · Reflections
            </div>
            <div style={{ color: INK, fontSize: 20, letterSpacing: -0.3 }}>
              Notes from the Library
            </div>
          </div>
          <button onClick={onClose} data-testid="my-notes-close"
            style={capsuleStyle({ active: false, size: "sm" })}>close</button>
        </div>

        <input
          value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search verse or word…"
          data-testid="my-notes-search"
          style={{
            width: "100%", padding: "12px 20px", borderRadius: 999, border: "none",
            background: "var(--fg-05)", color: INK, fontFamily: FONT, fontSize: 13,
            outline: "none", marginBottom: 14,
            boxShadow: `0 0 24px rgba(${accentRgb},0.16) inset`,
          }}
        />

        <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: "center", color: MUTED_36, fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase" }}>
              opening…
            </div>
          ) : items.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: MUTED_60, fontSize: 13, lineHeight: 1.6 }}>
              Nothing here yet. Ask Yuo…theologian inside a chapter, or write a margin note.
            </div>
          ) : (
            items.map((it) => (
              <div key={`${it.kind}-${it.id}`} data-testid={`note-item-${it.id}`}
                style={{
                  padding: 18, borderRadius: 24, marginBottom: 12,
                  background: glow(accentRgb, 0.10),
                  animation: "yuo-emerge .9s cubic-bezier(.2,.7,.2,1) both",
                }}>
                <div style={{ color: accent, fontSize: 12, letterSpacing: 0.18 }}>
                  {it.ref_human}
                </div>
                {it.verse_text && (
                  <div style={{ color: MUTED_60, fontStyle: "italic", fontSize: 12, marginTop: 6, lineHeight: 1.4 }}>
                    «{it.verse_text}»
                  </div>
                )}
                <div style={{
                  color: INK, fontSize: 13, lineHeight: 1.55, marginTop: 8,
                  whiteSpace: "pre-wrap", maxHeight: 130, overflow: "hidden",
                  maskImage: "linear-gradient(to bottom, black 70%, transparent)",
                }}>{it.body}</div>
                {onInsert && (
                  <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                    <button onClick={() => { onInsert(it.body); onClose(); }}
                      data-testid={`note-insert-reflection-${it.id}`}
                      style={capsuleStyle({ active: false, size: "sm", rgb: accentRgb })}>
                      Insert reflection
                    </button>
                    {it.verse_text && (
                      <button onClick={() => { onInsert(`${it.ref_human}\n«${it.verse_text}»\n\n${it.body}`); onClose(); }}
                        data-testid={`note-insert-verse-${it.id}`}
                        style={capsuleStyle({ active: false, size: "sm", rgb: accentRgb })}>
                        Insert verse + reflection
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// Kept for any component still importing BookIcon (Diary composer)
export function BookIcon({ color, size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M 4 4 L 12 4 L 12 20 L 4 20 Z" stroke={color} strokeWidth={1.5} fill="none" strokeLinejoin="round" />
      <path d="M 12 4 L 20 4 L 20 20 L 12 20" stroke={color} strokeWidth={1.5} fill="none" strokeLinejoin="round" />
    </svg>
  );
}
